/**
 * CSV export hook for DataList.
 *
 * Builds a CSV string from visible columns and current data, then triggers a
 * browser file download.  Column selection, header inclusion, and file name
 * are all configurable via `ToolbarConfig['export']`.
 */

import { useCallback } from 'react';

import type { ToolbarConfig, ColumnConfig } from '../types';
import { buildCsvString, downloadCsv } from '../utils/csvBuilder';
import type { CsvColumn } from '../utils/csvBuilder';

// ---------------------------------------------------------------------------
// Public return type
// ---------------------------------------------------------------------------

export interface UseCsvExportReturn {
  /** Trigger CSV export and browser download. */
  exportCsv: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Provides a single `exportCsv` action that generates a CSV from the current
 * visible columns + data and triggers a file download in the browser.
 *
 * @param exportConfig - The `toolbar.export` section of the DataList config.
 * @param visibleColumns - Resolved visible columns (ordered, non-hidden).
 * @param data - Current page of row data.
 * @param getCellValue - Value accessor matching the column resolver.
 * @param configId - Fallback file name when `exportConfig.fileName` is not set.
 */
export function useCsvExport(
  exportConfig: ToolbarConfig['export'] | undefined,
  visibleColumns: Array<{ key: string } & ColumnConfig>,
  data: Record<string, unknown>[],
  getCellValue: (row: Record<string, unknown>, columnKey: string) => unknown,
  configId: string,
): UseCsvExportReturn {
  const exportCsv = useCallback(() => {
    // 1. Determine which columns to export
    const exportColumnKeys = exportConfig?.columns;

    const columnsToExport = exportColumnKeys
      ? visibleColumns.filter((col) => exportColumnKeys.includes(col.key))
      : visibleColumns;

    // 2. Map to CsvColumn descriptors
    const csvColumns: CsvColumn[] = columnsToExport.map((col) => ({
      key: col.key,
      label: col.label,
    }));

    // 3. Build the CSV string
    const csvString = buildCsvString(csvColumns, data, getCellValue, {
      includeHeaders: exportConfig?.includeHeaders ?? true,
    });

    // 4. Determine file name and trigger download
    const fileName = exportConfig?.fileName ?? configId;

    downloadCsv(csvString, fileName);
  }, [exportConfig, visibleColumns, data, getCellValue, configId]);

  return { exportCsv };
}
